import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { path } from 'ramda';

import { Button } from 'js/components/common/Button';
import ErrorMessage from 'js/components/common/Fields/ErrorMessage';
import { Select } from 'js/components/common/Fields/Select';
import Form from 'js/components/common/Form';
import { I18n } from 'js/components/common/I18n';
import { LocalPopup } from 'js/components/common/Popup';

import { setPlayerLimitsInfo } from '@interlayer/js/app/store/reducers/ResponsibleGaming/actions';

import { usePlayerLimitInfo } from 'js/app/components/main/ResponsibleGaming/hooks/usePlayerLimitInfo';
import { fetchUpdateDepositLimits } from 'js/app/components/main/ResponsibleGaming/server';
import { useTranslations } from 'js/hooks/i18n';
import { componentService } from 'js/services/component.service';
import { useUserSelector } from 'js/store/hooks';

import { checkAvailablePeriod, useDepositLimitsConfig } from '../../hooks/useDepositLimitsConfig';

import style from './DepositLimitsPopup.scss';

export const DepositLimitsPopup = () => {
    const t = useTranslations('playtech.mobile.system.depositlimitpopup.');
    const [isShow, setIsShow] = useState(false);
    const [isShowForm, setIsShowForm] = useState(false);
    const dispatch = useDispatch();
    const [{ isLoading: isLoadingPlayerLimitInfo, data: { isDepositLimits } }, refetchLimitInfo] = usePlayerLimitInfo();
    const [{ data:
        {
            dTagDailyDefaultDepositLimit,
            dTagWeeklyDefaultDepositLimit,
            dTagMonthlyDefaultDepositLimit,
            allowedLimitsOptions,
            ...depositLimitsConfig
        }
    }] = useDepositLimitsConfig();
    const isDayLimit = checkAvailablePeriod('daily', depositLimitsConfig);
    const isWeekLimit = checkAvailablePeriod('weekly', depositLimitsConfig);
    const isMonthLimit = checkAvailablePeriod('monthly', depositLimitsConfig);
    const totalDepositCount = useUserSelector(path(['paymentsStatistics', 'deposits', 'totalDepositCount']));
    const ageVerificationStatus = useUserSelector(path(['userDetails', 'ageVerificationStatus']));

    useEffect(() => {
        if (
            !isLoadingPlayerLimitInfo
            && !isDepositLimits
            && totalDepositCount === '0'
            && ageVerificationStatus === 'passed'
        ) {
            setIsShow(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingPlayerLimitInfo, totalDepositCount]);

    const objectDefaultLimits = {
        daily: dTagDailyDefaultDepositLimit,
        weekly: dTagWeeklyDefaultDepositLimit,
        monthly: dTagMonthlyDefaultDepositLimit
    };

    const depositLimitPeriodOption = useMemo(() => {
        const options = [];

        if (isDayLimit) {
            options.push({
                value: 'daily',
                label: t('day')
            });
        }

        if (isWeekLimit) {
            options.push({
                value: 'weekly',
                label: t('week')
            });
        }

        if (isMonthLimit) {
            options.push({
                value: 'monthly',
                label: t('month')
            });
        }

        return options;
    }, [isDayLimit, isMonthLimit, isWeekLimit, t]);

    const updateDepositLimits = ({ amount, timePeriod }) => {
        const amountData = Number(amount);

        if (!amountData) {
            return t('nothingchanged');
        }

        return fetchUpdateDepositLimits([{
            amount: amountData,
            timePeriod
        }])
            .then(() => {
                dispatch(setPlayerLimitsInfo({}));
                refetchLimitInfo({});
                setIsShow(false);
            })
            .catch((error) => error.response?.data.errorMessage);
    };

    if (!isShow) {
        return null;
    }

    return (
        <LocalPopup
            title={t('depositlimit')}
            isModal
            className={style.containerPopup}
        >
            <I18n className={style.text} tKey="text" t={t} />

            <div className={style.buttons}>
                <Button
                    size="medium"
                    className={style.modalBtn}
                    onClick={() => setIsShowForm(true)}
                >
                    {t('yes')}
                </Button>

                <Button
                    size="medium"
                    view="link"
                    className={style.modalBtn}
                    onClick={() => setIsShow(false)}
                >
                    {t('no')}
                </Button>
            </div>

            {isShowForm && (
                <Form
                    onSubmit={updateDepositLimits}
                    render={({ submitting, handleSubmit, submitError, values: { timePeriod } }) => (
                        <form className={style.form} onSubmit={handleSubmit}>
                            <div className={style.errorWrap}>
                                <ErrorMessage
                                    error={submitError}
                                    animate
                                    className={style.error}
                                />
                            </div>

                            <div className={style.limitsRow}>
                                <div className={style.limits}>
                                    <div className={style.selectLabel}>
                                        {t('period')}
                                    </div>

                                    <Select
                                        className={style.select}
                                        classNames={{ content: style.selectContent }}
                                        name="timePeriod"
                                        options={depositLimitPeriodOption}
                                        defaultValue={depositLimitPeriodOption?.[0]?.value}
                                    />
                                </div>

                                <div className={style.limits}>
                                    <div className={style.selectLabel}>
                                        {t('amount')}
                                    </div>

                                    <div className={style.selectWrap}>
                                        <span className={style.currency}>£</span>

                                        <Select
                                            className={style.select}
                                            classNames={{
                                                content: style.selectContent,
                                                control: style.selectControl
                                            }}
                                            name="amount"
                                            options={allowedLimitsOptions}
                                            defaultValue={objectDefaultLimits[timePeriod]}
                                        />
                                    </div>
                                </div>
                            </div>

                            <Button
                                className={style.btnSubmit}
                                type="submit"
                                disabled={submitting}
                            >
                                {t('confirm')}
                            </Button>
                        </form>
                    )}
                />
            )}
        </LocalPopup>
    );
};

componentService.registerWebContentComponents({ DepositLimitsPopup });

